import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useData } from '../context/DataContext';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { DatePicker } from '../components/ui/DatePicker';
import { Checkbox } from '../components/ui/Checkbox';
import { CustomerSelect } from '../components/domain/CustomerSelect';
import { LineItemRow } from '../components/domain/LineItemRow';
import { TotalsPanel } from '../components/domain/TotalsPanel';
import { PaymentModal } from '../components/domain/PaymentModal';
import { QuoteApprovalModal } from '../components/domain/QuoteApprovalModal';
import { calculateSubtotal, calculateTaxAmount, calculateInvoiceTotal, calculateProfitMargin } from '../utils/calculations';
import { LineItem, InvoiceStatus } from '../types';
import { ArrowLeftIcon, PlusIcon, SaveIcon, SendIcon, CreditCardIcon, CheckCircleIcon } from 'lucide-react';
interface LineItemFormData {
  description: string;
  quantity: number;
  price: number;
  jobCost?: number;
  isTaxable: boolean;
}
export function InvoiceForm() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const isEditing = !!id;
  const {
    createInvoice,
    updateInvoice,
    getInvoiceById,
    getLineItemsByInvoiceId,
    selectedCompanyId,
    taxRate
  } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showQuoteApprovalModal, setShowQuoteApprovalModal] = useState(false);
  // Form state
  const [customerId, setCustomerId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [status, setStatus] = useState<InvoiceStatus>('draft');
  const [shippingCharge, setShippingCharge] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [sendViaEmail, setSendViaEmail] = useState(false);
  const [sendViaSMS, setSendViaSMS] = useState(false);
  const [lineItems, setLineItems] = useState<LineItemFormData[]>([{
    description: '',
    quantity: 1,
    price: 0,
    isTaxable: true
  }]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Load existing invoice data
  useEffect(() => {
    if (isEditing && id) {
      const invoice = getInvoiceById(id);
      if (invoice) {
        setCustomerId(invoice.customer_id);
        setDate(new Date(invoice.date).toISOString().split('T')[0]);
        setStatus(invoice.status);
        setShippingCharge(invoice.shipping_charge || 0);
        setDiscount(invoice.discount || 0);
        setSendViaEmail(invoice.sent_via_email || false);
        setSendViaSMS(invoice.sent_via_sms || false);
        const existingLineItems = getLineItemsByInvoiceId(id);
        if (existingLineItems.length > 0) {
          setLineItems(existingLineItems.map(li => ({
            description: li.description,
            quantity: li.quantity,
            price: li.price,
            jobCost: li.job_cost,
            isTaxable: li.is_taxable
          })));
        }
      }
    }
  }, [isEditing, id, getInvoiceById, getLineItemsByInvoiceId]);
  // Calculate totals
  const calculatedLineItems: LineItem[] = useMemo(() => {
    return lineItems.map((item, index) => ({
      id: `temp-${index}`,
      invoice_id: id || 'new',
      description: item.description,
      quantity: item.quantity,
      price: item.price,
      job_cost: item.jobCost,
      is_taxable: item.isTaxable,
      profit_margin: calculateProfitMargin(item.price, item.jobCost)
    }));
  }, [lineItems, id]);
  const subtotal = useMemo(() => calculateSubtotal(calculatedLineItems), [calculatedLineItems]);
  const taxAmount = useMemo(() => calculateTaxAmount(calculatedLineItems, taxRate), [calculatedLineItems, taxRate]);
  const total = useMemo(() => calculateInvoiceTotal(subtotal, taxAmount, shippingCharge, discount), [subtotal, taxAmount, shippingCharge, discount]);
  const addLineItem = () => {
    setLineItems([...lineItems, {
      description: '',
      quantity: 1,
      price: 0,
      isTaxable: true
    }]);
  };
  const removeLineItem = (index: number) => {
    setLineItems(lineItems.filter((_, i) => i !== index));
  };
  const updateLineItem = (index: number, updates: Partial<LineItemFormData>) => {
    setLineItems(lineItems.map((item, i) => i === index ? {
      ...item,
      ...updates
    } : item));
  };
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!customerId) {
      newErrors.customer = 'Please select a customer';
    }
    if (!date) {
      newErrors.date = 'Please select a date';
    }
    if (lineItems.length === 0) {
      newErrors.lineItems = 'Please add at least one line item';
    }
    const hasEmptyLineItem = lineItems.some(item => !item.description.trim() || item.price <= 0);
    if (hasEmptyLineItem) {
      newErrors.lineItems = 'Please fill in all line item details';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSave = async (sendAfterSave: boolean = false) => {
    if (!validate()) return;
    setIsLoading(true);
    try {
      const invoiceData = {
        company_id: selectedCompanyId,
        customer_id: customerId,
        date: new Date(date),
        status: sendAfterSave ? 'sent' as InvoiceStatus : status,
        subtotal,
        shipping_charge: shippingCharge || undefined,
        tax_rate: taxRate,
        tax_amount: taxAmount,
        discount: discount || undefined,
        total_amount: total,
        created_by: 'admin',
        sent_via_email: sendAfterSave ? true : sendViaEmail,
        sent_via_sms: sendViaSMS
      };
      const lineItemsData = lineItems.map(item => ({
        description: item.description,
        quantity: item.quantity,
        price: item.price,
        job_cost: item.jobCost,
        is_taxable: item.isTaxable,
        profit_margin: calculateProfitMargin(item.price, item.jobCost)
      }));
      if (isEditing && id) {
        await updateInvoice(id, invoiceData, lineItemsData);
      } else {
        await createInvoice(invoiceData, lineItemsData);
      }
      navigate('/invoices');
    } catch (error) {
      console.error('Failed to save invoice:', error);
    } finally {
      setIsLoading(false);
    }
  };
  const existingInvoice = isEditing && id ? getInvoiceById(id) : null;
  const statusOptions = [{
    value: 'draft',
    label: 'Draft'
  }, {
    value: 'quote',
    label: 'Quote'
  }, {
    value: 'sent',
    label: 'Sent'
  }, {
    value: 'paid',
    label: 'Paid'
  }, {
    value: 'approved',
    label: 'Approved'
  }, {
    value: 'refunded',
    label: 'Refunded'
  }, {
    value: 'voided',
    label: 'Voided'
  }];
  return <div className="space-y-6" data-id="element-364">
      {/* Page Header */}
      <div className="flex items-center gap-4" data-id="element-365">
        <Button variant="ghost" onClick={() => navigate('/invoices')} leftIcon={<ArrowLeftIcon className="w-4 h-4" data-id="element-367" />} data-id="element-366">
          Back
        </Button>
        <div className="flex-1" data-id="element-368">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-369">
            {isEditing ? 'Edit Invoice' : 'New Invoice'}
          </h1>
        </div>
        {isEditing && existingInvoice && existingInvoice.status !== 'quote' && <Button variant="secondary" onClick={() => setShowPaymentModal(true)} leftIcon={<CreditCardIcon className="w-4 h-4" data-id="element-371" />} data-id="element-370">
            Payments
          </Button>}
        {isEditing && existingInvoice && existingInvoice.status === 'quote' && <Button onClick={() => setShowQuoteApprovalModal(true)} leftIcon={<CheckCircleIcon className="w-4 h-4" data-id="element-373" />} data-id="element-372">
            Approve Quote
          </Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-374">
        {/* Main Form */}
        <div className="lg:col-span-2 space-y-6" data-id="element-375">
          {/* Invoice Details */}
          <Card data-id="element-376">
            <CardHeader title="Invoice Details" data-id="element-377" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-378">
              <CustomerSelect value={customerId} onChange={setCustomerId} error={errors.customer} data-id="element-379" />
              <DatePicker label="Invoice Date" value={date} onChange={e => setDate(e.target.value)} error={errors.date} data-id="element-380" />
              <Select label="Status" options={statusOptions} value={status} onChange={e => setStatus(e.target.value as InvoiceStatus)} data-id="element-381" />
            </div>
          </Card>

          {/* Line Items */}
          <Card data-id="element-382">
            <CardHeader title="Line Items" action={<Button variant="secondary" size="sm" onClick={addLineItem} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-385" />} data-id="element-384">
                  Add Item
                </Button>} data-id="element-383" />
            {errors.lineItems && <p className="text-sm text-red-600 mb-4" data-id="element-386">{errors.lineItems}</p>}
            <div className="space-y-4" data-id="element-387">
              {lineItems.map((item, index) => <LineItemRow key={index} index={index} description={item.description} quantity={item.quantity} price={item.price} jobCost={item.jobCost} isTaxable={item.isTaxable} onDescriptionChange={value => updateLineItem(index, {
              description: value
            })} onQuantityChange={value => updateLineItem(index, {
              quantity: value
            })} onPriceChange={value => updateLineItem(index, {
              price: value
            })} onJobCostChange={value => updateLineItem(index, {
              jobCost: value
            })} onTaxableChange={value => updateLineItem(index, {
              isTaxable: value
            })} onRemove={() => removeLineItem(index)} canRemove={lineItems.length > 1} data-id="element-388" />)}
            </div>
          </Card>

          {/* Additional Charges */}
          <Card data-id="element-389">
            <CardHeader title="Additional Charges" data-id="element-390" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-391">
              <Input label="Shipping Charge" type="number" min="0" step="0.01" value={shippingCharge || ''} onChange={e => setShippingCharge(parseFloat(e.target.value) || 0)} data-id="element-392" />
              <Input label="Discount" type="number" min="0" step="0.01" value={discount || ''} onChange={e => setDiscount(parseFloat(e.target.value) || 0)} data-id="element-393" />
            </div>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6" data-id="element-394">
          {/* Totals */}
          <Card data-id="element-395">
            <CardHeader title="Invoice Summary" data-id="element-396" />
            <TotalsPanel subtotal={subtotal} taxAmount={taxAmount} taxRate={taxRate} shippingCharge={shippingCharge} discount={discount} total={total} data-id="element-397" />
          </Card>

          {/* Send Options */}
          <Card data-id="element-398">
            <CardHeader title="Send Options" data-id="element-399" />
            <div className="space-y-3" data-id="element-400">
              <Checkbox label="Send via Email" checked={sendViaEmail} onChange={e => setSendViaEmail(e.target.checked)} data-id="element-401" />
              <Checkbox label="Send via SMS" checked={sendViaSMS} onChange={e => setSendViaSMS(e.target.checked)} data-id="element-402" />
            </div>
          </Card>

          {/* Actions */}
          <Card data-id="element-403">
            <div className="space-y-3" data-id="element-404">
              <Button className="w-full" onClick={() => handleSave(false)} isLoading={isLoading} leftIcon={<SaveIcon className="w-4 h-4" data-id="element-406" />} data-id="element-405">
                {isEditing ? 'Save Changes' : 'Save Draft'}
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => handleSave(true)} isLoading={isLoading} leftIcon={<SendIcon className="w-4 h-4" data-id="element-408" />} data-id="element-407">
                Save & Send
              </Button>
            </div>
          </Card>
        </div>
      </div>

      {/* Payment Modal */}
      {existingInvoice && existingInvoice.status !== 'quote' && <PaymentModal invoice={existingInvoice} isOpen={showPaymentModal} onClose={() => setShowPaymentModal(false)} data-id="element-409" />}

      {/* Quote Approval Modal */}
      {existingInvoice && existingInvoice.status === 'quote' && <QuoteApprovalModal invoice={existingInvoice} lineItems={calculatedLineItems} isOpen={showQuoteApprovalModal} onClose={() => setShowQuoteApprovalModal(false)} onApproved={() => {
      setShowQuoteApprovalModal(false);
      navigate('/invoices');
    }} data-id="element-410" />}
    </div>;
}